import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Zap, ShieldCheck, Eye, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { MarketItem } from '@/lib/api';
import { getSlugFromItemName, parseMarketHashName } from '@/lib/slug';

/** Детерминированный фейковый счётчик просмотров (seed = имя скина).
 *  60% товаров → 10–50 просмотров, 40% → 51–170. */
function getFakeViewCount(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  h = Math.abs(h);
  return (h % 10) < 6 ? 10 + (h % 41) : 51 + (h % 120);
}

/** Короткие подписи износа — в карточке нет места на «После полевых испытаний». */
const WEAR_SHORT: Record<string, string> = {
  'Factory New':    'Прямо с завода',
  'Minimal Wear':   'Немного поношенное',
  'Field-Tested':   'После полевых',
  'Well-Worn':      'Поношенное',
  'Battle-Scarred': 'Закалённое',
};

interface SkinCardProps {
  skin: MarketItem;
  viewMode?: 'grid' | 'list';
}

/** Parse "AK-47 | Redline (Field-Tested)" → "AK-47" */
function parseWeaponName(name: string): string {
  return name.split('|')[0].trim();
}

/** Parse "AK-47 | Redline (Field-Tested)" → "Redline" */
function parseSkinPattern(name: string): string | null {
  const parts = name.split('|');
  if (parts.length > 1) return parts[1].split('(')[0].trim() || null;
  return null;
}

export function SkinCard({ skin, viewMode = 'grid' }: SkinCardProps) {
  const rarity = skin._rarity ?? null;
  // delivery=1 у Xpanda — ручная выдача. Раньше всем товарам показывалась
  // молния «1 минута», включая те, что едут до 12 часов.
  const slowDelivery = skin._slow_delivery === true;
  const DeliveryIcon = slowDelivery ? Clock : Zap;
  const deliveryClass = slowDelivery ? 'text-amber-400' : 'text-green-400';
  const deliveryHint = slowDelivery
    ? 'Доставка до 12 часов'
    : 'Скорость доставки — 1 минута';
  const weaponName = parseWeaponName(skin.name);
  const skinPattern = parseSkinPattern(skin.name);
  const rarityColor = rarity?.color || '#6b7280';
  const skinName = skinPattern || skin.name.split('|')[1]?.split('(')[0].trim() || 'Обычный';
  const viewCount = getFakeViewCount(skin.name);

  // Генерируем slug для перехода на страницу карточки
  const slug = getSlugFromItemName(skin.name);

  // При пер-офферной фильтрации бэкенд отдаёт имя С ИЗНОСОМ
  // («AK-47 | Redline (Factory New)»), при обычной — каноническое, без него.
  // Показываем износ, когда он есть: иначе после фильтра «FN» карточки
  // выглядят одинаково и выдача кажется неотфильтрованной.
  const wear = parseMarketHashName(skin.name).wear || '';
  // И тянем износ на страницу товара — там свой фильтр качества, без этого
  // клик по отфильтрованной карточке показывал бы снова все износы.
  const href = wear
    ? `/market/${slug}?wear=${encodeURIComponent(wear)}`
    : `/market/${slug}`;


  if (viewMode === 'list') {
    return (
      <Link
        to={href}
        className="flex items-center gap-4 p-4 bg-[#161b22] rounded-xl border border-[#30363d] hover:border-[#ff6b00]/50 transition-colors cursor-pointer"
      >
        {/* Image */}
        <div className="w-20 h-20 flex-shrink-0 bg-[#0d1117] rounded-lg p-2">
          <img
            src={skin.image}
            alt={skin.name}
            className="w-full h-full object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2260%22%3E%3Crect fill=%22%231a2332%22 width=%2280%22 height=%2260%22/%3E%3Ctext fill=%22%23ff6b00%22 font-family=%22sans-serif%22 font-size=%2210%22 x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22%3ECS2%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-[#8b949e] text-sm">{weaponName}</p>
          <p className="text-white font-medium truncate">{skinName}</p>
          <p className="text-[#8b949e] text-xs mt-1">
            {wear ? `${WEAR_SHORT[wear] || wear} · ` : ''}В наличии: {skin.quantity}
          </p>
        </div>
        
        {/* Price + trust icons */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[#ff6b00] font-bold text-lg">от {(skin.price_rub_display ?? 0).toLocaleString('ru-RU')} ₽</p>
          </div>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <DeliveryIcon className={`w-5 h-5 ${deliveryClass} cursor-default flex-shrink-0`} />
              </TooltipTrigger>
              <TooltipContent>{deliveryHint}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-0.5 cursor-default">
                  <Eye className="w-5 h-5 text-[#8b949e] flex-shrink-0" />
                  <span className="text-xs text-[#8b949e]">{viewCount}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>Количество просмотров товара за 24 часа</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <ShieldCheck className="w-3.5 h-3.5 text-green-400 cursor-default flex-shrink-0" />
              </TooltipTrigger>
              <TooltipContent>Безопасный Trade процесс. Если вы не получили товар — средства возвращаются на ваш баланс</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="group relative block bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden cursor-pointer transition-all duration-300 hover:border-[#ff6b00]/50 hover:shadow-lg hover:shadow-[#ff6b00]/10 hover:-translate-y-1"
    >
      {/* Rarity Bar */}
      <div 
        className="h-1 w-full"
        style={{ backgroundColor: rarityColor }}
      />
      
      {/* Image Container */}
      <div className="relative p-4 bg-gradient-to-b from-[#1a2332]/50 to-transparent">
        {/* Оверлей: доставка — сверху-слева */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute top-2 left-2 p-1.5 bg-[#0d1117]/80 rounded-full cursor-default z-10">
              <DeliveryIcon className={`w-5 h-5 ${deliveryClass}`} />
            </div>
          </TooltipTrigger>
          <TooltipContent>{deliveryHint}</TooltipContent>
        </Tooltip>

        {/* Оверлей: просмотры — сверху-справа */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-[#0d1117]/80 rounded-full px-2 py-0.5 cursor-default z-10">
              <Eye className="w-4 h-4 text-[#8b949e]" />
              <span className="text-xs text-[#8b949e]">{viewCount}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>Количество просмотров товара за 24 часа</TooltipContent>
        </Tooltip>

        {/* Оверлей: автовозврат — снизу-слева */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute bottom-2 left-2 p-1 bg-[#0d1117]/80 rounded-full cursor-default z-10">
              <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
            </div>
          </TooltipTrigger>
          <TooltipContent>Безопасный Trade процесс. Если вы не получили товар — средства возвращаются на ваш баланс</TooltipContent>
        </Tooltip>

        {/* Skin Image */}
        <div className="aspect-square flex items-center justify-center p-2">
          <img
            src={skin.image}
            alt={skin.name}
            className="max-w-full max-h-full object-contain drop-shadow-lg transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              const t = e.target as HTMLImageElement;
              t.onerror = null;
              t.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%231a2332'/%3E%3Ctext x='100' y='75' font-family='sans-serif' font-size='13' fill='%23ff6b00' text-anchor='middle' dominant-baseline='middle'%3ECS2 Skin%3C/text%3E%3C/svg%3E";
            }}
          />
        </div>
      </div>
      
      {/* Info */}
      <div className="p-3 space-y-2">
        <div>
          <p className="text-[#8b949e] text-xs truncate">{weaponName}</p>
          <p className="text-white font-medium text-sm truncate">{skinName}</p>
          {wear && (
            <p className="text-[#6e7681] text-[11px] truncate">{WEAR_SHORT[wear] || wear}</p>
          )}
        </div>
        
        {/* Price */}
        <div>
          <p className="text-[#ff6b00] font-bold text-lg">от {(skin.price_rub_display ?? 0).toLocaleString('ru-RU')} ₽</p>
        </div>
      </div>
    </Link>
  );
}
